/**
 * Pure presentation rules for the tenancy Finanzen tab.
 *
 * NO ARITHMETIC OVER MONEY LIVES HERE, and that absence is the point. The
 * monthly figure is the server's `monthly_revenue_equivalent`; the recurring
 * and one-time splits come from `utils/tenancyRevenueBreakdown`, which the
 * tenant and unit surfaces already read. A sum computed in this file would be a
 * second answer to a question the backend has already answered — the shape
 * `AdminTenanciesPage`'s KPI guards exist to prevent.
 *
 * What IS here: turning three server facts into words. Nothing derives, infers
 * or fills a gap.
 */
import { monthLabel } from "../../../components/admin/bexio/bexioDraftLabels";

/**
 * The rent month of a Bexio invoice — ONLY when the backend proved one.
 *
 * `rent_period` reaches the wire from `bexio_invoice_draft_requests.rent_period`
 * and from nowhere else. NULL means no draft-ledger row ever recorded which
 * month this invoice covers, which is true of every hand-mapped historical
 * invoice. This returns null for that, and the caller renders no month line —
 * it does NOT fall back to `issue_date`, `due_date`, `document_nr` or the
 * title, each of which would be a guess presented as a fact.
 *
 * Parsed from the ISO string's own parts rather than through `new Date()`: a
 * `Date` built from "2026-10-01" is midnight UTC, and west of Greenwich it
 * formats as September. The month is a business fact, not a device one
 * (CLAUDE.md Regel 51).
 */
export function rentPeriodLabel(isoDate) {
  if (!isoDate) return null;
  const [y, m] = String(isoDate).split("-");
  const year = Number(y);
  const month = Number(m);
  if (!Number.isInteger(year) || !Number.isInteger(month)) return null;
  if (month < 1 || month > 12) return null;
  return monthLabel(year, month);
}

/**
 * WHY this invoice is on this tenancy.
 *
 * Not decoration: the two paths have different reliability and different
 * repair actions, so a support question starts here. `both` is a real state
 * rather than a rounding of either.
 */
export const LINK_SOURCE_LABEL = {
  manual_mapping: "Manuell zugeordnet",
  draft_ledger: "Aus Rechnungsentwurf",
  manual_mapping_and_draft_ledger: "Zugeordnet + Rechnungsentwurf",
};

/**
 * NEVER the raw token. A value this map does not know returns null and the row
 * simply omits the line.
 *
 * The earlier draft fell through to `source` so a contract change would be
 * visible — but the place it would have been visible is an operator's screen,
 * as `manual_mapping_and_draft_ledger`. This is secondary transparency
 * information, not a figure anyone acts on, so showing nothing is strictly
 * better than showing an identifier. A new backend value is caught by the
 * tests, which is where it belongs.
 */
export function linkSourceLabel(source) {
  if (!source) return null;
  return LINK_SOURCE_LABEL[source] || null;
}

/**
 * The deposit, in the words this repo already uses for it.
 *
 * `TenancyCard` renders exactly these four facts on the tenant surface —
 * "Erhalten" / "Erhalten am" / "Zurückgezahlt" / "Zurückgezahlt am" — and this
 * reads the same columns to say the same thing. Different wording for one
 * datum on two screens is how an operator ends up believing they are two data.
 *
 * The three states are the three the columns can express, and no more. There is
 * deliberately no "offen"/"überfällig": nothing on this wire says when a
 * deposit was DUE, so a lateness claim would be invented.
 */
/**
 * The deposit status, in the words this repo already uses for it.
 *
 * NOTHING IS DERIVED HERE ANY MORE (K4x.2). This file used to own a third
 * vocabulary — "Erhalten" / "Nicht erhalten" / "Zurückgezahlt" — computed from
 * `deposit_amount`, `deposit_received` and `deposit_returned`. Two problems,
 * both real: it read the LEGACY amount column, which the backend classifies as
 * `unresolved` rather than as a security; and it was type-blind, so an
 * insurance guarantee nobody ever "receives" was labelled "Nicht erhalten" as
 * though a payment were missing.
 *
 * The state now comes from `tenancy.security_state`, which the serializer fills
 * from the same helper the deposits dashboard uses, and the words come from the
 * shared label source. Its own docstring said it best before it stopped being
 * true: different wording for one datum on two screens is how an operator ends
 * up believing they are two data.
 */
export function depositState(tenancy) {
  return tenancy?.security_state ?? null;
}

/** The date belonging to the state above, or null. Never a substitute date. */
export function depositStateDate(tenancy) {
  const state = depositState(tenancy);
  if (state === "returned") return tenancy?.deposit_returned_at || null;
  // `held` and `active_guarantee` are both "we have it"; `pending` has no date
  // yet, and inventing one would assert a confirmation that has not happened.
  if (state === "held" || state === "active_guarantee") {
    return tenancy?.deposit_received_at || null;
  }
  return null;
}
